import { useRef, useState } from "react";
import { PlusIcon } from "lucide-react";
import type { CmmdProjectUpdateStatus, CmmdProjectUpdateView } from "@cmmd-forge/contracts";

import { CmmdProjectUpdateCard } from "./CmmdProjectUpdateCard";
import { OVERVIEW_SECTION_HEADING_CLASS } from "./overviewSectionHeading";
import { ProjectUpdateComposerFields } from "./ProjectUpdateComposerFields";
import { Skeleton } from "~/components/ui/skeleton";

export interface ProjectUpdatesSectionProps {
  readonly projectId: string;
  readonly title?: string;
  readonly density?: "default" | "overview";
  readonly updates: readonly CmmdProjectUpdateView[];
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly onAdd: (input: {
    readonly status: CmmdProjectUpdateStatus;
    readonly body: string;
  }) => Promise<void>;
  readonly onUpdate: (
    updateId: string,
    input: { readonly status: CmmdProjectUpdateStatus; readonly body: string },
  ) => Promise<void>;
  readonly onDelete: (updateId: string) => Promise<void>;
}

export function ProjectUpdatesSection({
  projectId,
  title = "Updates",
  density = "default",
  updates,
  loading = false,
  error: loadError = null,
  onAdd,
  onUpdate,
  onDelete,
}: ProjectUpdatesSectionProps) {
  const [composing, setComposing] = useState(false);
  const [status, setStatus] = useState<CmmdProjectUpdateStatus>("on_track");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addUpdateButtonRef = useRef<HTMLButtonElement>(null);
  const closeComposer = () => {
    setComposing(false);
    setBody("");
    setError(null);
    requestAnimationFrame(() => addUpdateButtonRef.current?.focus());
  };

  const submit = async () => {
    if (!body.trim() || body.length > 10_000) return;
    setSaving(true);
    setError(null);
    try {
      await onAdd({ status, body });
      setBody("");
      setStatus("on_track");
      setComposing(false);
      requestAnimationFrame(() => addUpdateButtonRef.current?.focus());
    } catch {
      setError("Couldn’t post this update. Your draft is still here—try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className={OVERVIEW_SECTION_HEADING_CLASS}>{title}</h2>
        {composing ? null : (
          <button
            ref={addUpdateButtonRef}
            type="button"
            onClick={() => setComposing(true)}
            className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <PlusIcon className="size-3" />
            Add update
          </button>
        )}
      </div>

      {composing ? (
        <div className="mb-3">
          <ProjectUpdateComposerFields
            projectId={projectId}
            status={status}
            body={body}
            saving={saving}
            error={error}
            ariaLabel="Project status update"
            submitLabel="Post update"
            submittingLabel="Posting…"
            onStatusChange={setStatus}
            onBodyChange={setBody}
            onSubmit={() => void submit()}
            onCancel={closeComposer}
          />
        </div>
      ) : null}

      {loadError ? (
        <p
          role="alert"
          className="mb-3 rounded-md border border-destructive-border bg-destructive-surface p-3 text-sm text-destructive"
        >
          {loadError}
        </p>
      ) : null}

      {loading && updates.length === 0 ? (
        <ProjectUpdatesSkeleton />
      ) : updates.length === 0 ? (
        composing || loadError ? null : (
          <p className="text-sm text-muted-foreground/60">No updates yet.</p>
        )
      ) : (
        <div className="space-y-4">
          {updates.map((update) => (
            <CmmdProjectUpdateCard
              key={update.id}
              projectId={projectId}
              update={update}
              density={density}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectUpdatesSkeleton() {
  return (
    <div aria-label="Loading project updates" className="space-y-4">
      {["first", "second", "third"].map((key) => (
        <div key={key} className="rounded-xl bg-muted/25 p-4">
          <Skeleton className="h-3 w-28 rounded-md" />
          <Skeleton className="mt-4 h-4 w-4/5 rounded-md" />
          <Skeleton className="mt-2 h-4 w-2/3 rounded-md" />
          <Skeleton className="mt-5 h-3 w-32 rounded-md" />
        </div>
      ))}
    </div>
  );
}
